import {
  FaGlobe,
  FaLinkedinIn,
  FaTwitter,
  FaInstagram,
  FaFacebookF,
} from 'react-icons/fa';
import { UPLOADS_BASE, slugifyDoctorName } from '../api/doctorApi';
import defaultDoctorImg from '../assets/images/docter-profile.png';

const SOCIAL_FIELDS = [
  { key: 'website', label: 'Website', icon: FaGlobe, field: 'website_address' },
  { key: 'linkedin', label: 'LinkedIn', icon: FaLinkedinIn, field: 'linkedin' },
  { key: 'twitter', label: 'Twitter', icon: FaTwitter, field: 'x_profile' },
  { key: 'instagram', label: 'Instagram', icon: FaInstagram, field: 'instagram' },
  { key: 'facebook', label: 'Facebook', icon: FaFacebookF, field: 'facebook_page' },
];

export function resolveDegreeLabels(degreeIds, degreesMaster = []) {
  if (!degreeIds) return [];
  return degreeIds
    .toString()
    .split(',')
    .map((id) => degreesMaster.find((d) => String(d.medical_degree_id) === id.trim()))
    .filter(Boolean)
    .map((d) => d.medical_degree_title || d.medical_degree || d.degree_name || `Degree #${d.medical_degree_id}`);
}

export function resolveSpecializationLabels(specializationIds, specializationsMaster = []) {
  if (!specializationIds) return [];
  return specializationIds
    .toString()
    .split(',')
    .map((id) => specializationsMaster.find((s) => String(s.specialization_id) === id.trim()))
    .filter(Boolean)
    .map((s) => s.specialization);
}

function getExperienceYears(registrationYear) {
  if (!registrationYear) return null;
  const years = Math.max(0, new Date().getFullYear() - Number(registrationYear));
  return years > 0 ? `${years}+ Years Experience` : null;
}

const KNOWN_CITIES = [
  'Mumbai', 'Delhi', 'New Delhi', 'Chennai', 'Bangalore', 'Bengaluru',
  'Kolkata', 'Hyderabad', 'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow',
  'Chandigarh', 'Kochi', 'Coimbatore', 'Indore', 'Bhopal', 'Nagpur',
];

function parseCityFromAddress(address) {
  if (!address) return null;

  const normalized = address.toLowerCase();
  const knownCity = KNOWN_CITIES.find((city) => normalized.includes(city.toLowerCase()));
  if (knownCity) return knownCity;

  const parts = address.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length < 2) return null;

  const lastPart = parts[parts.length - 1];
  if (!lastPart || lastPart.length > 40 || /^\d{5,}/.test(lastPart)) return null;
  return lastPart;
}

export function resolveHospitalAndLocation(doc) {
  const hospitalName = doc.establishment_name?.trim() || null;
  const location = (
    doc.city_name
    || doc.establishment_city_name
    || doc.location_name
    || parseCityFromAddress(doc.address)
    || doc.state_name
    || doc.establishment_state_name
  )?.trim() || null;

  return {
    hospitalName,
    location,
    label: [hospitalName, location].filter(Boolean).join(', ') || '—',
  };
}

function getProfileImage(profilePicPath, { noDefaults = false } = {}) {
  if (profilePicPath) return `${UPLOADS_BASE}/uploads/${profilePicPath}`;
  return noDefaults ? null : defaultDoctorImg;
}

function buildConnectLinks(doc) {
  return SOCIAL_FIELDS
    .map(({ key, label, icon, field }) => ({
      key,
      label,
      icon,
      url: doc[field] || '',
    }))
    .filter((link) => link.url);
}

export function mapDoctorToCard(doc, { specializations = [], noDefaults = false } = {}) {
  const specialtyLabels = resolveSpecializationLabels(doc.specialization_ids, specializations);
  const experience = getExperienceYears(doc.registration_year);
  const roleParts = [doc.medical_specialty || doc.designation, experience].filter(Boolean);
  const { hospitalName, location, label } = resolveHospitalAndLocation(doc);
  const defaultRole = noDefaults ? '' : (doc.designation || 'Medical Expert');
  const defaultSpecializes = noDefaults ? '' : (doc.short_profile || 'General practice');

  return {
    id: doc.doctor_id,
    slug: slugifyDoctorName(doc.doctor_name, doc.doctor_id),
    name: doc.doctor_name || (noDefaults ? '' : 'Doctor'),
    role: roleParts.join(' | ') || defaultRole,
    avatar: getProfileImage(doc.profile_pic_path, { noDefaults }),
    hospital: label === '—' && noDefaults ? '' : label,
    hospitalName,
    location,
    specializes: specialtyLabels.length
      ? specialtyLabels.join(', ')
      : defaultSpecializes,
    medicalSpecialty: doc.medical_specialty || '',
  };
}

export function mapDoctorToProfile(doc, { degrees = [], specializations = [] } = {}) {
  const qualifications = resolveDegreeLabels(doc.degree_ids, degrees);
  const specialtyLabels = resolveSpecializationLabels(doc.specialization_ids, specializations);
  const experienceYears = doc.registration_year
    ? `${Math.max(0, new Date().getFullYear() - Number(doc.registration_year))} yrs experience`
    : '—';

  const hospitalLocation = resolveHospitalAndLocation(doc);
  const subtitleParts = [
    hospitalLocation.label !== '—' ? hospitalLocation.label : null,
    qualifications.slice(0, 3).join(', '),
  ].filter(Boolean);

  const phones = Array.isArray(doc.phone_numbers)
    ? doc.phone_numbers.filter(Boolean)
    : [doc.phone_number_1, doc.phone_number_2].filter(Boolean);

  const addressParts = [doc.address, doc.address_line_2, doc.city_name, doc.state_name, doc.country_name]
    .filter(Boolean);

  const about = doc.long_profile
    || doc.short_profile
    || (specialtyLabels.length ? `${doc.doctor_name} specializes in ${specialtyLabels.join(', ')}.` : '');

  return {
    id: doc.doctor_id,
    slug: slugifyDoctorName(doc.doctor_name, doc.doctor_id),
    name: doc.doctor_name || 'Doctor',
    avatar: getProfileImage(doc.profile_pic_path),
    prefix: [doc.designation, doc.department_name].filter(Boolean).join(' — ') || doc.medical_specialty || '',
    specialty: doc.medical_specialty || doc.designation || '',
    subtitle: subtitleParts.join(' · '),
    experience: experienceYears,
    consultFee: '—',
    language: '—',
    about,
    qualifications: qualifications.length ? qualifications : ['—'],
    introductionVideo: doc.video_bio_url || '',
    publications: doc.articles_published ? `${doc.articles_published} published article(s)` : '—',
    news: '—',
    awards: [],
    fees: { consultation: '—', followUp: '—' },
    registration: {
      council: doc.registration_year ? `Registered ${doc.registration_year}` : '—',
      number: doc.registration_certificate_path ? 'Certificate on file' : '—',
    },
    contact: {
      phone: phones[0] ? `${doc.country_code ? `${doc.country_code} ` : ''}${phones[0]}` : '—',
      email: doc.email_address || '—',
      address: addressParts.join(', ') || '—',
    },
    connect: buildConnectLinks(doc),
    profilePdfPath: doc.profile_pdf_path || '',
  };
}

export function getSpecialtyNames(specialties = []) {
  return specialties
    .map((s) => s.medical_specialty)
    .filter(Boolean);
}
